import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Save, Plus, X } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { Printer } from "../../../types/printer"; 
import { PrinterLanguage } from "../../../types/label";

interface PrinterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  printer: Printer | null;
  onSave: (data: any) => void;
}

export const PrinterDialog: React.FC<PrinterDialogProps> = ({ isOpen, onClose, printer, onSave }) => {
  const [formData, setFormData] = useState({ 
    name: "", 
    language: "ZPL" as PrinterLanguage, 
    dpi: 203, 
    ipAddress: "", 
    macAddress: "", 
    isActive: true 
  });

  useEffect(() => {
    if (!isOpen) return;
    if (printer) {
      setFormData({
        name: printer.name,
        language: printer.language,
        dpi: printer.dpi || 203,
        ipAddress: printer.ipAddress || "",
        macAddress: printer.macAddress || "",
        isActive: printer.isActive
      });
    } else {
      setFormData({ name: "", language: "ZPL", dpi: 203, ipAddress: "", macAddress: "", isActive: true });
    }
  }, [isOpen, printer]);

  const handleSubmit = () => {
    onSave(formData);
  };

  const languageOptions = [
    { label: "ZPL", value: "ZPL" },
    { label: "TSPL", value: "TSPL" },
    { label: "ESC/POS", value: "ESC_POS" },
    { label: "PDF", value: "PDF" },
  ];

  // Ortak input stili
  const inputClass = "w-full px-3 py-2 bg-inputBg border border-inputBorder rounded-md text-inputText focus:ring-1 focus:ring-inputFocus focus:border-inputFocus outline-none transition-all";

  return (
    <Dialog
      header={printer ? "Yazıcı Düzenle" : "Yeni Yazıcı Tanımla"}
      visible={isOpen} 
      onHide={onClose} 
      modal 
      className="w-full md:w-[500px] bg-card text-text rounded-xl shadow-lg border border-borderColor"
    >
      <div className="flex flex-col gap-4 py-4">
        {/* Yazıcı Adı */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">Yazıcı Adı *</label>
          <InputText 
            value={formData.name} 
            className={inputClass}
            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} 
          />
        </div>

        {/* Dil ve DPI (Yan yana) */}
        <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-text">Dil</label>
                <Dropdown 
                  value={formData.language} 
                  options={languageOptions} 
                  className={inputClass + " p-0"} // Dropdown'un padding'ini dengeler
                  onChange={(e) => setFormData(p => ({ ...p, language: e.value }))} 
                />
            </div>
            <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-text">DPI</label>
                <InputNumber 
                  value={formData.dpi} 
                  className={inputClass}
                  onValueChange={(e) => setFormData(p => ({ ...p, dpi: e.value || 203 }))} 
                />
            </div>
        </div>

        {/* IP ve MAC */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">IP Adresi</label>
          <InputText 
            value={formData.ipAddress || ""} 
            className={inputClass}
            onChange={(e) => setFormData(p => ({ ...p, ipAddress: e.target.value }))} 
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">MAC Adresi</label>
          <InputText 
            value={formData.macAddress || ""} 
            className={inputClass}
            onChange={(e) => setFormData(p => ({ ...p, macAddress: e.target.value }))} 
          />
        </div>

        {/* Aktiflik */}
        <div className="flex items-center gap-2 pt-2">
          <Checkbox 
            inputId="isActive"
            checked={formData.isActive} 
            onChange={(e) => setFormData(p => ({ ...p, isActive: e.checked || false }))} 
          />
          <label htmlFor="isActive" className="text-sm text-text cursor-pointer">Yazıcı Aktif</label>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 pt-4 border-t border-borderColor mt-2">
        <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
        <IconTextButton onClick={handleSubmit} icon={printer ? Save : Plus} className="btn-submit">
          {printer ? "Güncelle" : "Kaydet"}
        </IconTextButton>
      </div>
    </Dialog>
  );
};